/**
 * Paywall overlay shown when the user's 15-day freemium plan has expired.
 * Renders as a full-screen modal that blocks app interaction; the only
 * actions available are "Subscribe" and "Logout".
 *
 * The status check (locked / days_left) lives in a tiny hook that polls
 * `/api/freemium/status` every minute while the user is signed in.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import { Lock, Crown, Sparkles, AlertCircle, LogOut } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useFreemiumStatus = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState({ enrolled: false, locked: false, days_left: null });

  const refresh = useCallback(async () => {
    if (!user || user.role === 'admin') {
      setStatus({ enrolled: false, locked: false, days_left: null });
      return;
    }
    try {
      const { data } = await axios.get(`${API}/freemium/status`);
      setStatus(data || { enrolled: false, locked: false, days_left: null });
    } catch {
      /* fail silent — never lock a user due to a network blip */
    }
  }, [user]);

  useEffect(() => {
    refresh();
    if (!user || user.role === 'admin') return;
    const id = setInterval(refresh, 60_000);
    return () => clearInterval(id);
  }, [user, refresh]);

  return { ...status, refresh };
};

const FreemiumLockOverlay = () => {
  const { locked, days_left, enrolled } = useFreemiumStatus();
  const { logout } = useAuth();
  const location = useLocation();

  // Allow the user to reach the subscription page even while locked, so they
  // can actually convert. Also skip the paywall on PayPal callback pages.
  const onSubscriptionPage = location.pathname.startsWith('/subscription');
  const onCoachPage = location.pathname.startsWith('/coach/');

  // Not locked: render a tiny "days left" banner instead. Only when the user
  // is enrolled and within the window, to nudge conversion.
  if (!locked) {
    if (!enrolled || days_left == null || days_left > 7) return null;
    return (
      <div className="sticky top-0 z-30 -mx-6 lg:-mx-10 -mt-6 lg:-mt-10 mb-6 px-6 lg:px-10 py-2.5 bg-gradient-to-r from-amber-500/15 via-amber-400/10 to-amber-500/15 border-b border-amber-400/30" data-testid="freemium-banner">
        <div className="flex items-center justify-between gap-3 flex-wrap text-sm">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
            <p className="text-amber-100">
              <span className="font-bold">{days_left} {days_left === 1 ? 'día' : 'días'}</span> restantes de tu prueba pre-temporada
            </p>
          </div>
          <Link
            to="/subscription"
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-amber-400 text-black text-xs font-bold hover:bg-amber-300 transition-colors"
            data-testid="freemium-banner-cta"
          >
            <Crown className="w-3 h-3" /> Hazte premium
          </Link>
        </div>
      </div>
    );
  }

  // Locked: full screen overlay. Allow the subscription / coach pages to
  // remain interactable so users can complete the conversion path.
  if (onSubscriptionPage || onCoachPage) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-[#09090b]/95 backdrop-blur-xl flex items-center justify-center p-6" data-testid="freemium-lock-overlay">
      <div className="max-w-lg w-full rounded-3xl bg-zinc-950 border border-zinc-800 overflow-hidden">
        <div className="p-8 sm:p-10 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#ccff00]/15 flex items-center justify-center mb-5">
            <Lock className="w-7 h-7 text-[#ccff00]" />
          </div>
          <p className="text-xs uppercase tracking-[3px] text-[#ccff00] font-bold mb-3">Tu prueba ha terminado</p>
          <h1 className="text-3xl sm:text-4xl font-black mb-4">¿Listo para el siguiente nivel?</h1>
          <p className="text-zinc-400 leading-relaxed mb-8">
            Has completado tus 15 días de prueba pre-temporada. Para seguir entrenando con un plan
            <span className="text-white font-semibold"> personalizado por Alex</span>, suscríbete y desbloquea
            el plan completo, métricas avanzadas, integraciones con Strava y comunicación directa con el coach.
          </p>

          <ul className="text-left space-y-2 mb-8 text-sm">
            {[
              'Plan semanal personalizado a tu objetivo',
              'Métricas avanzadas: CTL, TSS, FTP, fatiga',
              'Sincronización automática con Strava',
              'Comunicación directa con el coach',
              'Biblioteca de PDFs y vídeos exclusivos',
            ].map((line, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <Sparkles className="w-4 h-4 text-[#ccff00] mt-0.5 flex-shrink-0" />
                <span className="text-zinc-200">{line}</span>
              </li>
            ))}
          </ul>

          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/subscription" className="flex-1" data-testid="freemium-lock-subscribe">
              <Button className="btn-primary w-full text-base py-6">
                <Crown className="w-4 h-4 mr-2" /> Ver planes
              </Button>
            </Link>
            <Link to="/coach/alex-diaz" className="flex-1" data-testid="freemium-lock-meet-coach">
              <Button variant="outline" className="btn-secondary w-full text-base py-6">
                Conoce a tu coach
              </Button>
            </Link>
          </div>

          <button
            onClick={logout}
            className="mt-6 inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
            data-testid="freemium-lock-logout"
          >
            <LogOut className="w-3 h-3" /> Cerrar sesión
          </button>
        </div>
      </div>
    </div>
  );
};

export default FreemiumLockOverlay;
